import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { formatCents } from '@/lib/format/currency';
import {
  formatAccountDisqualificationErrorValues,
  hasNothingToRestore,
  mapAccountDisqualificationError,
  resolveAccountReleaseAction,
  resolveRestoreDialogState,
} from '@/lib/campaigns/accountDisqualification';
import {
  ADMIN_CPM_ACCOUNT_RESTORE_PREVIEW,
  ADMIN_LIFT_CPM_CAMPAIGN_ACCOUNT_DISQUALIFICATION,
  ADMIN_RESTORE_CPM_CAMPAIGN_ACCOUNT_CLIPS,
} from '@/queries/accountDisqualification';
import type { CpmAccountRestorePreview } from '@/generated/account-disqualification';

type Target = { id: string; account_label: string; lifted_at?: string | null };
type Props = {
  disqualification: Target | null;
  mode: 'lift' | 'restore';
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
  /** Campaign currency for the money lines. */
  currency?: string;
};

const PREFIX = 'admin.campaigns.accountDisqualification';

function LiftBody({ disqualification, mode, onOpenChange, onDone, currency = 'BRL' }: Props & { disqualification: Target }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [restoreTicked, setRestoreTicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const restoreClips = mode === 'restore' || restoreTicked;
  const { data, loading, error } = useQuery<{ adminCpmAccountRestorePreview: CpmAccountRestorePreview }>(
    ADMIN_CPM_ACCOUNT_RESTORE_PREVIEW,
    { variables: { disqualificationId: disqualification.id }, fetchPolicy: 'no-cache' },
  );
  const [lift, { loading: lifting }] = useMutation(ADMIN_LIFT_CPM_CAMPAIGN_ACCOUNT_DISQUALIFICATION);
  const [restore, { loading: restoring }] = useMutation(ADMIN_RESTORE_CPM_CAMPAIGN_ACCOUNT_CLIPS);
  const pending = lifting || restoring;
  const preview = data?.adminCpmAccountRestorePreview ?? null;

  // A plain lift needs no preview; a ticked lift with nothing restorable is a plain lift.
  const action = resolveAccountReleaseAction({ mode, preview, restoreTicked });
  const { sendRestore } = action;
  const canConfirm = action.canConfirm && !pending;
  const nothing = !!preview && hasNothingToRestore(preview, restoreClips);
  const state = preview ? resolveRestoreDialogState(preview, { restoreClips: sendRestore, forceCapped: false }) : null;
  // What a cap-limited restore would do: it can clear a budget shortfall the full restore has.
  const forcedState = preview ? resolveRestoreDialogState(preview, { restoreClips: true, forceCapped: true }) : null;
  // "Restaurar com limite" is offered while the per-clipper cap is the obstacle and the capped amounts fit.
  const offerForce = !!preview && sendRestore && !!state?.showForce && !nothing && !!forcedState?.canConfirm;

  const submit = async (force: boolean) => {
    if (pending || (force ? !offerForce : !canConfirm)) return;
    try {
      if (mode === 'restore') {
        await restore({ variables: { disqualificationId: disqualification.id, forceCapped: force } });
      } else {
        await lift({ variables: { disqualificationId: disqualification.id, restoreClips: sendRestore, forceCapped: force } });
      }
      toast({ title: t(`${PREFIX}.${mode === 'restore' ? 'successRestored' : 'successLifted'}`) });
      onOpenChange(false);
      onDone();
    } catch (err) {
      const { key, values } = mapAccountDisqualificationError(err);
      setErrorMessage(t(key, formatAccountDisqualificationErrorValues(values, currency)));
    }
  };

  const blockerState = state?.showForce && sendRestore ? forcedState : state;
  const blockerText = preview && blockerState?.blocker
    ? t(`${PREFIX}.blockers.${blockerState.blocker}`, { amount: formatCents(Math.max(0, blockerState.budgetChargeCents - preview.budget_remaining_cents), currency) })
    : null;

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t(`${PREFIX}.${mode === 'restore' ? 'restoreTitle' : 'liftTitle'}`, { account: disqualification.account_label })}</DialogTitle>
          </DialogHeader>
          {mode === 'lift' && (
            <div className="flex items-start gap-2">
              <Checkbox id="account-dq-restore" checked={restoreTicked} disabled={pending} onCheckedChange={value => setRestoreTicked(value === true)} />
              <Label htmlFor="account-dq-restore" className="text-sm leading-snug">{t(`${PREFIX}.restoreClips`)}</Label>
            </div>
          )}
          {/* Only a restore waits for the preview; a plain lift goes ahead without it. */}
          {restoreClips && loading && <Loader2 aria-hidden="true" className="mx-auto h-5 w-5 animate-spin" />}
          {restoreClips && error && <p role="alert" className="text-sm text-destructive">{t(`${PREFIX}.previewLoadError`)}</p>}
          {preview && restoreClips && (
            <div className="space-y-1 rounded-md border bg-muted/20 p-3 text-sm">
              <p>{t(`${PREFIX}.restorableCount`, { count: preview.restorable_count })}</p>
              {preview.skipped_count > 0 && <p>{t(`${PREFIX}.skippedCount`, { count: preview.skipped_count })}</p>}
              {nothing ? <p>{t(`${PREFIX}.nothingToRestore`)}</p> : state && <>
                <p>{t(`${PREFIX}.restoreAmount`, { amount: formatCents(state.restoreCents, currency) })}</p>
                <p>{t(`${PREFIX}.budgetCharge`, { amount: formatCents(state.budgetChargeCents, currency) })}</p>
                <p>{t(`${PREFIX}.budgetRemaining`, { amount: formatCents(preview.budget_remaining_cents, currency) })}</p>
                {state.showForce && <p className="text-amber-500">{t(`${PREFIX}.forceCappedHint`, { amount: formatCents(preview.over_cap_cents, currency) })}</p>}
              </>}
            </div>
          )}
          {blockerText && <p role="alert" className="text-sm text-destructive">{blockerText}</p>}
          <DialogFooter>
            <Button variant="ghost" disabled={pending} onClick={() => onOpenChange(false)}>{t(`${PREFIX}.cancel`)}</Button>
            {offerForce && (
              <Button disabled={pending} onClick={() => void submit(true)}>
                {pending && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                {t(`${PREFIX}.restoreWithCap`)}
              </Button>
            )}
            <Button disabled={!canConfirm} onClick={() => void submit(false)}>
              {pending && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
              {t(`${PREFIX}.${mode === 'restore' ? 'confirmRestore' : 'confirmLift'}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${PREFIX}.${mode === 'restore' ? 'restoreAction' : 'liftAction'}`)}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function LiftAccountDisqualificationDialog(props: Props) {
  return props.disqualification
    ? <LiftBody key={`${props.disqualification.id}:${props.mode}`} {...props} disqualification={props.disqualification} />
    : null;
}
